import { CONSTANTS } from 'config/constants';
import { CustomerSetting } from 'models/customer-setting';
import { customerSettings } from 'utils/customerSettings';

/**
 * Fallback for when the organization's settings were not loaded. The served default is
 * the TREATING_CLINICIAN_TITLE_WORDS defaultValue in shared-denthub-code's settings.json,
 * which backend fills in; shared's isTreatingClinicianTitle applies the same rule server side.
 */
const DEFAULT_TREATING_CLINICIAN_TITLE_WORDS = ['LÄKARE', 'HYGIENIST'];

const parseTitleWords = (settingValue: unknown): unknown => {
    if (typeof settingValue !== 'string') {
        return settingValue;
    }
    try {
        return JSON.parse(settingValue);
    } catch {
        return undefined;
    }
};

// Stored Object values get their first space turned into '_' by the backend; undo that.
const normalizeTitleWord = (titleWord: string): string => titleWord.replace(/_/g, ' ').trim().toUpperCase();

/** The title words that make a user a treating clinician (dentist) in action-based orders. */
export const getTreatingClinicianTitleWords = (organizationSettings: CustomerSetting[]): string[] => {
    const settingValue = parseTitleWords(
        customerSettings.getCustomerSetting(
            organizationSettings,
            CONSTANTS.SETTINGS_NAMES.TREATING_CLINICIAN_TITLE_WORDS,
        ),
    );
    const titleWords = Array.isArray(settingValue)
        ? settingValue
              .filter((titleWord): titleWord is string => typeof titleWord === 'string')
              .map(normalizeTitleWord)
              .filter((titleWord) => titleWord.length > 0)
        : [];

    return titleWords.length > 0 ? titleWords : DEFAULT_TREATING_CLINICIAN_TITLE_WORDS;
};

/** A user without a title counts as a treating clinician. */
export const isTreatingClinicianTitle = (title: string | null | undefined, titleWords: string[]): boolean => {
    if (!title) {
        return true;
    }
    const normalizedTitle = title.trim().toUpperCase();
    return titleWords.some((titleWord) => normalizedTitle.includes(titleWord));
};
